import React, { useEffect, useState } from "react";
import { Button, Card, Checkbox, Col, DatePicker, Form, Icon, Input, InputNumber, message, Modal, Row, Select, Spin, Upload } from "antd";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { createData, getData } from "../../../util/API";
import {
  attachStorePhotosToPayload,
  getStorePhotoFiles,
  uploadPresignedStorePhotos,
} from "./vendorLeadUpload";
import "../../CustomerProfiling/ProductList.css";
import "./PartnerRegistration.css";

const PO_DATA_ENDPOINT = "/management/po-data/";
const VENDOR_SHEET_ENDPOINT = "/partner/vendor-sheet/";
const VENDOR_LEAD_ENDPOINT = "/partner/vendor-lead/";
const FREELANCER_ROLE = "freelancer";
const PARTNER_REGISTRATION_PATH = "/partner-registration";

const VENDOR_SHEET_STATUS = {
  REJECTED: "rejected",
  CALL_ME_LATER: "call me later",
  REGISTERED: "registered",
};

const DEFAULT_LEAD_REMARKS = {
  [VENDOR_SHEET_STATUS.REGISTERED]: "Customer registered successfully",
  [VENDOR_SHEET_STATUS.CALL_ME_LATER]: "Customer asked to call later",
  [VENDOR_SHEET_STATUS.REJECTED]: "Customer not interested",
};

const IST_UTC_OFFSET_MINUTES = 330;

const getNowIST = () => moment().utcOffset(IST_UTC_OFFSET_MINUTES);

const getDefaultRescheduledIST = () =>
  getNowIST().add(3, "days").hour(10).minute(0).second(0);

const toISTMoment = (value) => {
  if (!value || !moment.isMoment(value) || !value.isValid()) return null;
  return value.clone().utcOffset(IST_UTC_OFFSET_MINUTES, true);
};

const formatRescheduledIST = (value) => {
  const ist = toISTMoment(value);
  if (!ist) return "";
  return ist.format("YYYY-MM-DD HH:mm:ss");
};

const isBeforeNowIST = (value) => {
  const ist = toISTMoment(value);
  if (!ist) return true;
  return ist.isBefore(getNowIST());
};

const isCallMeLaterLeadStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase() === VENDOR_SHEET_STATUS.CALL_ME_LATER;

const formatRescheduledDisplay = (dateStr) => {
  if (!dateStr) return "";
  const parsed = moment(dateStr, "YYYY-MM-DD HH:mm:ss", true);
  if (parsed.isValid()) {
    return parsed.utcOffset(IST_UTC_OFFSET_MINUTES, true).format("DD MMM YYYY, hh:mm A");
  }
  const fallback = moment(dateStr);
  if (!fallback.isValid()) return String(dateStr);
  return fallback.utcOffset(IST_UTC_OFFSET_MINUTES, true).format("DD MMM YYYY, hh:mm A");
};
const PARTNER_SKILLTAG_LISTING_ENDPOINT = "/partner/skilltag-listing/";
const PARTNER_SERVICE_CATEGORY_ENDPOINT = "/partner/service-category/";

const DEFAULT_SERVICE_CATEGORIES = [
  "Enterprise",
  "solar",
  "ev",
  "Home appliances",
  "Smart appliances",
  "Telecom",
];

const normalizeCoveragePincodes = (values) => {
  const pins = [];
  (values || []).forEach((value) => {
    const pin = String(value).replace(/\D/g, "").slice(0, 6);
    if (pin.length === 6 && !pins.includes(pin)) {
      pins.push(pin);
    }
  });
  return pins;
};

const { Option } = Select;

const FIELD_ORDER = [
  "name",
  "email",
  "mobile",
  "whatsapp",
  "address",
  "pincode",
  "city",
  "serviceCategories",
  "skillTags",
  "serviceAreaCoverage",
  "engineerCount",
];

const parsePhoneDigits = (value) => {
  const trimmed = String(value || "").trim();
  let digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("+91") || (digits.startsWith("91") && digits.length > 10)) {
    digits = digits.slice(2);
  }
  return digits.slice(0, 10);
};

const sanitizePhoneInput = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("+91")) {
    const national = trimmed.slice(3).replace(/\D/g, "").slice(0, 10);
    return national ? `+91${national}` : "+91";
  }
  if (trimmed.startsWith("+")) {
    const afterPlus = trimmed.slice(1).replace(/\D/g, "");
    if (afterPlus.startsWith("91")) {
      const national = afterPlus.slice(2, 12);
      return national ? `+91${national}` : "+91";
    }
    return `+${afterPlus.slice(0, 10)}`;
  }
  return trimmed.replace(/\D/g, "").slice(0, 10);
};

const validatePhone = (value, label) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return `${label} is required.`;
  }
  if (/[^\d+\s]/.test(trimmed)) {
    return `${label} can only contain numbers and +91 prefix.`;
  }
  const digits = parsePhoneDigits(trimmed);
  if (digits.length < 10) {
    return `${label} must be 10 digits (excluding +91).`;
  }
  if (digits.length !== 10) {
    return `${label} must be exactly 10 digits.`;
  }
  if (!/^[6-9]\d{9}$/.test(digits)) {
    return `${label} is not a valid phone number.`;
  }
  return null;
};

const validateEmail = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return "Email is required.";
  }
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(trimmed)) {
    return "Please enter a valid email address.";
  }
  return null;
};

const formItemLayout = {
  labelCol: { xs: 24, sm: 24, style: { lineHeight: "normal", paddingLeft: 0, paddingRight: 0, marginBottom: 0 } },
  wrapperCol: { xs: 24, sm: 24, style: { paddingLeft: 0, paddingRight: 0 } },
};

const normalizeServiceCategoriesFromLead = (data) => {
  const raw =
    data?.Service_Categories ??
    data?.service_categories ??
    data?.["Service Categories"];
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeLeadData = (data) => {
  if (!data || typeof data !== "object") {
    return {
      tempId: "",
      companyName: "",
      partnerName: "",
      email: "",
      phone: "",
      status: "",
      city: "",
      address: "",
      serviceCategories: [],
      rescheduledDate: "",
      remark: "",
    };
  }
  const tempId = data["Temp ID"] ?? data.Temp_ID ?? data.temp_id ?? "";
  const companyName =
    data.Company_Name ?? data["Company Name"] ?? data.company_name ?? "";
  const partnerName = String(
    data.Partner_Name ?? data["Partner Name"] ?? data.partner_name ?? ""
  ).trim();
  const email =
    data.Email ?? data.email ?? data["Email Address"] ?? data.email_address ?? "";
  const phone =
    data.Phone_Number ?? data["Phone Number"] ?? data.phone_number ?? "";
  const status = data.Status ?? data.status ?? "";
  const city = data.City ?? data.city ?? "";
  const address =
    data.Address ??
    data.address ??
    data.Full_Address ??
    data.full_address ??
    data["Full Address"] ??
    "";
  const serviceCategories = normalizeServiceCategoriesFromLead(data);
  const rescheduledDate =
    data.rescheduled_date ?? data.rescheduledDate ?? data.Rescheduled_Date ?? "";
  const remark = data.Remark ?? data.remark ?? data.remarks ?? "";

  return {
    tempId,
    companyName,
    partnerName,
    email: String(email).trim(),
    phone,
    status,
    city,
    address: String(address).trim(),
    serviceCategories,
    rescheduledDate,
    remark,
  };
};

const SectionTitle = ({ title, optional }) => (
  <h3 className="pr-section-title">
    {title}
    {optional ? <span className="pr-optional-badge">Optional</span> : null}
  </h3>
);

const buildLeadSummaryChips = (lead) =>
  [
    lead.tempId ? { label: "Temp ID", value: String(lead.tempId) } : null,
    lead.companyName ? { label: "Company", value: String(lead.companyName).trim() } : null,
    lead.partnerName ? { label: "Name", value: String(lead.partnerName).trim() } : null,
  ].filter(Boolean);

const LEAD_BANNER_STYLES = {
  rescheduled: {
    icon: "clock-circle",
    className: "pr-lead-banner--rescheduled",
  },
  lead: {
    icon: "solution",
    className: "pr-lead-banner--lead",
  },
  error: {
    icon: "exclamation-circle",
    className: "pr-lead-banner--error",
  },
};

const LeadStatusBanner = ({ alert }) => {
  if (!alert) return null;

  const config = LEAD_BANNER_STYLES[alert.variant] || LEAD_BANNER_STYLES.lead;

  return (
    <div
      className={`pr-lead-banner ${config.className}`}
      role="status"
      aria-live="polite"
    >
      <div className="pr-lead-banner__icon" aria-hidden="true">
        <Icon type={config.icon} />
      </div>
      <div className="pr-lead-banner__content">
        {alert.highlight ? (
          <p className="pr-lead-banner__line">
            <strong>{alert.title}</strong>
            <span className="pr-lead-banner__line-sep">·</span>
            Rescheduled for <strong>{alert.highlight}</strong>
          </p>
        ) : (
          <p className="pr-lead-banner__line">
            <strong>{alert.title}</strong>
          </p>
        )}
        {alert.description ? (
          <p className="pr-lead-banner__description">{alert.description}</p>
        ) : null}
        {alert.chips?.length > 0 ? (
          <div className="pr-lead-banner__meta">
            {alert.chips.map((chip) => (
              <span key={`${chip.label}-${chip.value}`} className="pr-lead-banner__meta-item">
                <span className="pr-lead-banner__meta-label">{chip.label}</span>
                <span className="pr-lead-banner__meta-value">{chip.value}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const PartnerRegistration = () => {
  const history = useHistory();
  const [sameAsMobile, setSameAsMobile] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userRole = localStorage.getItem("user_role");

    if (!token) {
      history.replace("/signin", { from: { pathname: PARTNER_REGISTRATION_PATH } });
      return;
    }

    if (userRole !== FREELANCER_ROLE) {
      history.replace("/not-authorized");
    }
  }, [history]);

  const [mobile, setMobile] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [pincode, setPincode] = useState("");
  const [city, setCity] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [district, setDistrict] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [pinLoading, setPinLoading] = useState(false);
  const [pinError, setPinError] = useState(false);
  const [storePhotos, setStorePhotos] = useState([]);

  const [userId, setUserId] = useState("");

  const [leadLoading, setLeadLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [leadAlert, setLeadAlert] = useState(null);
  const [leadMeta, setLeadMeta] = useState({
    currentRecord: null,
    nextRecord: 0,
    totalPending: 0,
  });
  const [activeTempId, setActiveTempId] = useState("");
  const [currentLeadData, setCurrentLeadData] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [callLaterModalOpen, setCallLaterModalOpen] = useState(false);
  const [callLaterRemarks, setCallLaterRemarks] = useState("");
  const [nextContactDate, setNextContactDate] = useState(null);
  const [companyName, setCompanyName] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceCategoryOptions, setServiceCategoryOptions] = useState(
    DEFAULT_SERVICE_CATEGORIES
  );
  const [serviceCategoriesLoading, setServiceCategoriesLoading] = useState(false);
  const [skillTags, setSkillTags] = useState([]);
  const [skillOptions, setSkillOptions] = useState([]);
  const [skillOptionsLoading, setSkillOptionsLoading] = useState(false);
  const [serviceAreaCoverage, setServiceAreaCoverage] = useState([]);
  const [engineerCount, setEngineerCount] = useState(undefined);
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const fieldItemProps = (field) => ({
    validateStatus: fieldErrors[field] ? "error" : "",
    help: fieldErrors[field] || undefined,
  });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const pageTop = document.querySelector(".pr-page--standalone");
    if (pageTop) {
      pageTop.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToFirstError = (errors) => {
    const firstField = FIELD_ORDER.find((key) => errors[key]);
    if (!firstField) return;
    const el = document.querySelector(`[data-pr-field="${firstField}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = el.querySelector(
        "input:not([disabled]), .ant-select-selection, .ant-input-number-input"
      );
      if (focusable && typeof focusable.focus === "function") {
        setTimeout(() => focusable.focus(), 300);
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!name.trim()) {
      errors.name = "Please enter partner name.";
    }

    const emailError = validateEmail(email);
    if (emailError) errors.email = emailError;

    const mobileError = validatePhone(mobile, "Mobile number");
    if (mobileError) errors.mobile = mobileError;

    if (!sameAsMobile && String(whatsapp || "").trim()) {
      const whatsappError = validatePhone(whatsapp, "WhatsApp number");
      if (whatsappError) errors.whatsapp = whatsappError;
    }

    if (!pincode) {
      errors.pincode = "Please enter pincode.";
    } else if (!/^\d{6}$/.test(pincode)) {
      errors.pincode = "Pincode must be a 6-digit number.";
    } else if (pinLoading) {
      errors.pincode = "Please wait for pincode lookup to finish.";
    } else if (pinError) {
      errors.pincode = "Invalid pincode.";
    } else if (!pinLookupReady) {
      errors.pincode = "Please enter a valid pincode.";
    }

    if (!city.trim()) {
      errors.city = "Please enter city.";
    }

    if (!fullAddress.trim()) {
      errors.address = "Please enter full address.";
    }

    if (!serviceCategories.length) {
      errors.serviceCategories = "Please select at least one service category.";
    }

    if (!skillTags.length) {
      errors.skillTags = "Please select at least one skill tag.";
    }

    if (!serviceAreaCoverage.length) {
      errors.serviceAreaCoverage = "Please add at least one service area pincode.";
    }

    if (engineerCount == null || engineerCount === "") {
      errors.engineerCount = "Please enter number of engineers.";
    } else if (!Number.isInteger(Number(engineerCount)) || Number(engineerCount) < 1) {
      errors.engineerCount = "Number of engineers must be at least 1.";
    }

    return errors;
  };

  useEffect(() => {
    let cancelled = false;
    setSkillOptionsLoading(true);
    setServiceCategoriesLoading(true);

    Promise.all([
      getData(PARTNER_SKILLTAG_LISTING_ENDPOINT),
      getData(PARTNER_SERVICE_CATEGORY_ENDPOINT),
    ])
      .then(([skillRes, categoryRes]) => {
        if (cancelled) return;
        const tags = skillRes?.data?.tags;
        setSkillOptions(Array.isArray(tags) ? tags : []);

        const categories = categoryRes?.data?.service_categories?.filter(Boolean);
        setServiceCategoryOptions(
          Array.isArray(categories) && categories.length > 0
            ? categories
            : DEFAULT_SERVICE_CATEGORIES
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setSkillOptions([]);
        setServiceCategoryOptions(DEFAULT_SERVICE_CATEGORIES);
        message.error(err?.toString?.() || "Failed to load form options.");
      })
      .finally(() => {
        if (!cancelled) {
          setSkillOptionsLoading(false);
          setServiceCategoriesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const pincodeComplete = pincode.length === 6;
  const pinLookupReady = pincodeComplete && !pinLoading && !pinError && !!district;

  useEffect(() => {
    if (pincode.length !== 6) {
      setDistrict("");
      setState("");
      setCountry("");
      setPinError(false);
      setPinLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPinLoading(true);
    setPinError(false);

    getData(`${PO_DATA_ENDPOINT}?pin=${pincode}`)
      .then((res) => {
        if (cancelled) return;
        const pos = res?.data?.pos;
        if (pos?.length > 0 && pos[0].is_valid) {
          setDistrict(pos[0].district || "");
          setState(pos[0].state || "");
          setCountry(pos[0].country || "");
          setPinError(false);
        } else {
          setDistrict("");
          setState("");
          setCountry("");
          setPinError(true);
          message.error(res?.data?.message || "Invalid pincode. Please check and try again.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setDistrict("");
        setState("");
        setCountry("");
        setPinError(true);
        message.error(err?.toString?.() || "Failed to fetch pincode details.");
      })
      .finally(() => {
        if (!cancelled) setPinLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pincode]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    clearFieldError("email");
  };

  const handleMobileChange = (e) => {
    const value = sanitizePhoneInput(e.target.value);
    setMobile(value);
    clearFieldError("mobile");
    if (sameAsMobile) {
      setWhatsapp(value);
      clearFieldError("whatsapp");
    }
  };

  const handleWhatsappChange = (e) => {
    setWhatsapp(sanitizePhoneInput(e.target.value));
    clearFieldError("whatsapp");
  };

  const handleSameAsMobileChange = (e) => {
    const checked = e.target.checked;
    setSameAsMobile(checked);
    if (checked) {
      setWhatsapp(mobile);
      clearFieldError("whatsapp");
    }
  };

  const handlePincodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPincode(value);
    clearFieldError("pincode");
  };

  const handleCityChange = (e) => {
    setCity(e.target.value);
    clearFieldError("city");
  };

  const handleFullAddressChange = (e) => {
    setFullAddress(e.target.value);
    clearFieldError("address");
  };

  const handleStorePhotoUpload = (file) => {
    setStorePhotos((prev) => [...prev, file]);
    return false;
  };

  const handleStorePhotoRemove = (file) => {
    setStorePhotos((prev) => prev.filter((item) => item.uid !== file.uid));
  };

  const handleCoveragePincodesChange = (values) => {
    const normalized = normalizeCoveragePincodes(values);
    const dropped = (values || []).length - normalized.length;
    if (dropped > 0) {
      message.warning("Only valid 6-digit pincodes are kept.");
    }
    setServiceAreaCoverage(normalized);
    clearFieldError("serviceAreaCoverage");
  };

  const applyLeadToForm = (data) => {
    const lead = normalizeLeadData(data);
    if (lead.tempId) {
      setActiveTempId(String(lead.tempId));
    }
    if (lead.companyName) setCompanyName(String(lead.companyName).trim());
    if (lead.partnerName) setName(String(lead.partnerName).trim());

    if (lead.email) {
      setEmail(String(lead.email).trim());
      clearFieldError("email");
    } else {
      setEmail("");
    }

    if (lead.city) {
      setCity(String(lead.city));
      clearFieldError("city");
    } else {
      setCity("");
    }

    if (lead.address) {
      setFullAddress(String(lead.address));
      clearFieldError("address");
    } else {
      setFullAddress("");
    }

    if (lead.serviceCategories.length > 0) {
      setServiceCategories(lead.serviceCategories);
      setServiceCategoryOptions((prev) => [
        ...new Set([...prev, ...lead.serviceCategories]),
      ]);
      clearFieldError("serviceCategories");
    } else {
      setServiceCategories([]);
    }

    const phoneDigits = parsePhoneDigits(lead.phone);
    const altDigits = parsePhoneDigits(
      data.Alternate_Phone_Number ?? data["Alternate Phone Number"] ?? ""
    );
    if (phoneDigits) {
      const formatted = `+91${phoneDigits}`;
      setMobile(formatted);
      if (altDigits && altDigits !== phoneDigits) {
        setSameAsMobile(false);
        setWhatsapp(`+91${altDigits}`);
      } else if (sameAsMobile) {
        setWhatsapp(formatted);
      }
    }

    const chips = buildLeadSummaryChips(lead);

    if (isCallMeLaterLeadStatus(lead.status) && lead.rescheduledDate) {
      setLeadAlert({
        variant: "rescheduled",
        title: "Call me later",
        highlight: formatRescheduledDisplay(lead.rescheduledDate),
        chips,
      });
      return;
    }

    if (chips.length > 0) {
      setLeadAlert({
        variant: "lead",
        title: "Lead loaded",
        chips,
      });
      return;
    }

    setLeadAlert(null);
  };

  const showLeadFetchError = (errorMessage, isTempIdLookup) => {
    setLeadAlert({
      variant: "error",
      title: "Could not load lead",
      description: errorMessage,
    });
    message.warning(errorMessage);
    if (isTempIdLookup) {
      const lookupEl = document.querySelector("[data-pr-field='tempIdLookup']");
      if (lookupEl) {
        lookupEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const fetchVendorSheet = async ({ offset, tempId, quiet = false } = {}) => {
    setLeadLoading(true);
    setLeadAlert(null);

    const isTempIdLookup = tempId != null && String(tempId).trim() !== "";

    try {
      const params = new URLSearchParams();
      if (isTempIdLookup) {
        params.set("temp_id", String(tempId).trim());
      } else {
        params.set("offset", String(offset ?? 0));
      }

      const res = await getData(`${VENDOR_SHEET_ENDPOINT}?${params.toString()}`);
      const body = res?.data;
      const isSuccess = body && (body.status === true || body.status === "true");

      if (!res || !isSuccess) {
        const errorMessage =
          body?.message ||
          (isTempIdLookup
            ? "You are not allocated to this record"
            : "No more records available.");
        showLeadFetchError(errorMessage, isTempIdLookup);
        setLeadMeta((prev) => ({
          ...prev,
          nextRecord: isTempIdLookup ? prev.nextRecord : offset ?? prev.nextRecord,
        }));
        return false;
      }

      setCurrentLeadData(body.data);
      applyLeadToForm(body.data);
      setLeadMeta({
        currentRecord: body.current_record ?? 0,
        nextRecord: body.next_record ?? 0,
        totalPending: body.total_pending_records ?? 0,
      });
      if (!quiet) {
        message.success("Lead loaded.");
      }
      return true;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        (isTempIdLookup
          ? "You are not allocated to this record"
          : err?.toString?.() || "Failed to fetch lead.");
      showLeadFetchError(errorMessage, isTempIdLookup);
      return false;
    } finally {
      setLeadLoading(false);
    }
  };

  const resetFormForNextLead = () => {
    setSameAsMobile(false);
    setUserId("");
    setLeadAlert(null);
    setActiveTempId("");
    setCurrentLeadData(null);
    setCompanyName("");
    setName("");
    setEmail("");
    setMobile("");
    setWhatsapp("");
    setPincode("");
    setCity("");
    setFullAddress("");
    setDistrict("");
    setState("");
    setCountry("");
    setServiceCategories([]);
    setSkillTags([]);
    setServiceAreaCoverage([]);
    setEngineerCount(undefined);
    setPinError(false);
    setPinLoading(false);
    setStorePhotos([]);
    setFieldErrors({});
  };

  const buildLeadData = ({ status = "", rescheduled = "" } = {}) => {
    const base = currentLeadData || {};
    const tempId = activeTempId || base["Temp ID"] || base.Temp_ID || base.temp_id;
    const phoneDigits = parsePhoneDigits(mobile);
    const altDigits = sameAsMobile ? "" : parsePhoneDigits(whatsapp);
    const altFromSheet = base.Alternate_Phone_Number ?? base["Alternate Phone Number"] ?? "";

    return {
      "Temp ID": tempId != null && tempId !== "" ? Number(tempId) : base["Temp ID"],
      Company_Name:
        companyName ||
        base.Company_Name ||
        base["Company Name"] ||
        base.company_name ||
        "",
      Partner_Name:
        name.trim() ||
        base.Partner_Name ||
        base["Partner Name"] ||
        base.partner_name ||
        "",
      Email:
        email.trim() ||
        base.Email ||
        base.email ||
        base["Email Address"] ||
        "",
      Phone_Number: phoneDigits
        ? Number(phoneDigits)
        : base.Phone_Number ?? base["Phone Number"] ?? "",
      Alternate_Phone_Number: altDigits
        ? Number(altDigits)
        : altFromSheet
          ? Number(String(altFromSheet).replace(/\D/g, "").slice(-10)) || ""
          : "",
      Alloted_To: base.Alloted_To || base["Alloted_To"] || base.alloted_to || "",
      Status: status || "",
      next_contact: "",
      rescheduled: rescheduled || "",
      Pincode: pincode || "",
      City: city || "",
      Address: fullAddress.trim(),
      State: state || "",
      District: district || "",
      Country: country || "",
      Service_Categories: Array.isArray(serviceCategories) ? serviceCategories : [],
      Partner_Skill_Tags: Array.isArray(skillTags) ? skillTags : [],
      Service_Area_Pincodes: Array.isArray(serviceAreaCoverage)
        ? serviceAreaCoverage
        : [],
      No_Of_Engineers:
        engineerCount != null && engineerCount !== ""
          ? Number(engineerCount)
          : "",
    };
  };

  const buildVendorLeadPayload = (status, { remarks = "", rescheduled = "" } = {}) => {
    const isRegistered = status === VENDOR_SHEET_STATUS.REGISTERED;
    const isRejected = status === VENDOR_SHEET_STATUS.REJECTED;
    const isCallLater = status === VENDOR_SHEET_STATUS.CALL_ME_LATER;

    return {
      temp_id: Number(activeTempId),
      status,
      lead_data: buildLeadData(
        isCallLater
          ? { status, rescheduled }
          : { status }
      ),
      is_approved: isRegistered,
      is_rejected: isRejected,
      remarks: remarks || DEFAULT_LEAD_REMARKS[status] || "",
    };
  };

  const submitVendorLead = async (
    payload,
    { advanceNext = true, action = null, scrollOnSuccess = true } = {}
  ) => {
    if (!activeTempId) {
      message.warning("No lead loaded.");
      return false;
    }

    if (action) setLoadingAction(action);
    setActionLoading(true);

    const photoFiles = getStorePhotoFiles(storePhotos);
    const jsonPayload = attachStorePhotosToPayload(payload, storePhotos);

    try {
      // Step 1: save lead and receive presigned URLs
      const res = await createData(VENDOR_LEAD_ENDPOINT, jsonPayload);
      const body = res?.data;

      if (!res || body?.status === false) {
        const errorMessage = body?.message || "Failed to submit lead.";
        setLeadAlert({
          variant: "error",
          title: "Submission failed",
          description: errorMessage,
        });
        message.warning(errorMessage);
        return false;
      }

      const presignedUrls = body?.presigned_urls || [];

      if (photoFiles.length > 0) {
        if (!presignedUrls.length) {
          throw new Error("No presigned upload URLs returned for store photos.");
        }
        // Step 2: upload to S3; Step 3: confirm each upload
        await uploadPresignedStorePhotos(photoFiles, presignedUrls);
      }

      message.success(body?.message || "Lead submitted successfully.");

      if (advanceNext) {
        resetFormForNextLead();
        await fetchVendorSheet({ offset: 0, quiet: true });
      }

      if (scrollOnSuccess) {
        scrollToTop();
      }
      return true;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || err?.message || err?.toString?.() || "Failed to submit lead.";
      message.error(errorMessage);
      return false;
    } finally {
      setActionLoading(false);
      setLoadingAction(null);
    }
  };

  const clearActionLoading = () => {
    setActionLoading(false);
    setLoadingAction(null);
  };

  const handleReject = () => {
    if (!activeTempId) {
      message.warning("No lead loaded.");
      return;
    }
    clearActionLoading();
    setRejectRemarks(DEFAULT_LEAD_REMARKS[VENDOR_SHEET_STATUS.REJECTED]);
    setRejectModalOpen(true);
  };

  const handleRejectCancel = () => {
    setRejectModalOpen(false);
    clearActionLoading();
  };

  const handleRejectConfirm = () => {
    if (!rejectRemarks.trim()) {
      message.warning("Please enter remarks for rejection.");
      return Promise.reject();
    }
    const payload = buildVendorLeadPayload(VENDOR_SHEET_STATUS.REJECTED, {
      remarks: rejectRemarks.trim(),
    });
    return submitVendorLead(payload, {
      advanceNext: true,
      action: "reject",
      scrollOnSuccess: false,
    }).then((ok) => {
      if (ok) {
        setRejectModalOpen(false);
        setRejectRemarks("");
        window.setTimeout(() => scrollToTop(), 200);
      } else {
        return Promise.reject();
      }
      return ok;
    });
  };

  const handleCallMeLater = () => {
    if (!activeTempId) {
      message.warning("No lead loaded.");
      return;
    }
    clearActionLoading();
    setCallLaterRemarks(DEFAULT_LEAD_REMARKS[VENDOR_SHEET_STATUS.CALL_ME_LATER]);
    setNextContactDate(getDefaultRescheduledIST());
    setCallLaterModalOpen(true);
  };

  const handleCallMeLaterCancel = () => {
    setCallLaterModalOpen(false);
    setNextContactDate(null);
    clearActionLoading();
  };

  const handleCallMeLaterConfirm = () => {
    if (!nextContactDate) {
      message.warning("Please select next contact date and time.");
      return Promise.reject();
    }
    if (isBeforeNowIST(nextContactDate)) {
      message.warning("Please select a future date and time.");
      return Promise.reject();
    }
    if (!callLaterRemarks.trim()) {
      message.warning("Please enter remarks.");
      return Promise.reject();
    }
    const payload = buildVendorLeadPayload(VENDOR_SHEET_STATUS.CALL_ME_LATER, {
      remarks: callLaterRemarks.trim(),
      rescheduled: formatRescheduledIST(nextContactDate),
    });
    return submitVendorLead(payload, {
      advanceNext: true,
      action: "callLater",
      scrollOnSuccess: false,
    }).then((ok) => {
      if (ok) {
        setCallLaterModalOpen(false);
        setCallLaterRemarks("");
        setNextContactDate(null);
        window.setTimeout(() => scrollToTop(), 200);
      } else {
        return Promise.reject();
      }
      return ok;
    });
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstField = FIELD_ORDER.find((key) => errors[key]);
      message.error(errors[firstField]);
      scrollToFirstError(errors);
      return;
    }

    setFieldErrors({});
    setLoadingAction("submit");
    setActionLoading(true);
    const payload = buildVendorLeadPayload(VENDOR_SHEET_STATUS.REGISTERED, {
      remarks: DEFAULT_LEAD_REMARKS[VENDOR_SHEET_STATUS.REGISTERED],
    });
    await submitVendorLead(payload, { advanceNext: true, action: "submit" });
  };

  useEffect(() => {
    if (!localStorage.getItem("token")) return undefined;
    fetchVendorSheet({ offset: 0 });
    return undefined;
    // Initial lead fetch on page load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    history.replace("/signin");
  };

  const handleLoadLeadByUserId = async () => {
    const id = userId.trim();
    if (!id) {
      message.warning("Please enter Temp ID.");
      return;
    }
    if (!/^\d+$/.test(id)) {
      message.warning("Temp ID must be a number.");
      return;
    }

    await fetchVendorSheet({ tempId: id });
  };

  return (
    <div className="pl-hub-wrapper pr-page pr-page--standalone">
      <div className="pl-hub-container">
        <div className="pl-hub-header pr-header">
          <div className="pl-hub-title-row">
            <div className="pl-hub-icon">
              <Icon type="solution" />
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="pl-hub-title">Partner Registration</h2>
              <p className="pl-hub-subtitle">
                Complete the form below to register as a service partner.
              </p>
            </div>
            <div className="pr-header-actions">
              <Button
                type="link"
                icon="logout"
                onClick={handleLogout}
                className="pr-logout"
                style={{display: 'none'}}
              >
                Logout
              </Button>
              <Button
                type="link"
                icon="arrow-left"
                onClick={() => history.goBack()}
                className="pr-back"
                style={{display: 'none'}}
              >
                Back
              </Button>
            </div>
          </div>
        </div>

        <div className="pr-lookup-bar pr-lead-bar" data-pr-field="tempIdLookup">
          <Input.Group compact className="pr-lookup-group">
            <Input
              placeholder="Temp ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value.replace(/\D/g, ""))}
              onPressEnter={handleLoadLeadByUserId}
              prefix={<Icon type="idcard" style={{ color: "rgba(0,0,0,.25)" }} />}
            />
            <Button
              icon="search"
              loading={leadLoading}
              onClick={handleLoadLeadByUserId}
              style={{ margin: 0 }}
            >
              Load
            </Button>
          </Input.Group>
          <LeadStatusBanner alert={leadAlert} />
        </div>

        <Card className="pr-card" bordered={false}>
          <Form layout="vertical" className="pr-form" onSubmit={handleSubmit}>
            <div className="pr-block">
              <SectionTitle title="Basic information" />              
              <Row gutter={20} type="flex" className="pr-three-col-row">
                {companyName ? (
                  <Col xs={24} md={8} data-pr-field="name">                    
                      <Form.Item {...formItemLayout} label="Company name">
                        <Input size="large" value={companyName} readOnly />
                      </Form.Item>
                  </Col>
                ) : null}
                <Col xs={24} md={8} data-pr-field="name">
                  <Form.Item
                    {...formItemLayout}
                    {...fieldItemProps("name")}
                    label="Name"
                    required
                  >
                    <Input
                      placeholder="e.g. Rajesh Kumar"
                      size="large"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        clearFieldError("name");
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} data-pr-field="mobile">
                  <Form.Item
                    {...formItemLayout}
                    {...fieldItemProps("mobile")}
                    label="Mobile number"
                    required
                  >
                    <Input
                      placeholder="e.g. 9876543210 or +919876543210"
                      size="large"
                      maxLength={13}
                      value={mobile}
                      onChange={handleMobileChange}
                      inputMode="tel"
                      prefix={<Icon type="phone" style={{ color: "rgba(0,0,0,.25)" }} />}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} data-pr-field="whatsapp">
                  <Form.Item
                    {...formItemLayout}
                    {...fieldItemProps("whatsapp")}
                    label="WhatsApp number"
                  >
                    <Input
                      placeholder="e.g. 9876543210 or +919876543210"
                      size="large"
                      maxLength={13}
                      value={whatsapp}
                      onChange={handleWhatsappChange}
                      disabled={sameAsMobile}
                      inputMode="tel"
                      prefix={<Icon type="message" style={{ color: "rgba(0,0,0,.25)" }} />}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} data-pr-field="email">
                  <Form.Item
                    {...formItemLayout}
                    {...fieldItemProps("email")}
                    label="Email"
                    required
                  >
                    <Input
                      placeholder="e.g. partner@example.com"
                      size="large"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      inputMode="email"
                      prefix={<Icon type="mail" style={{ color: "rgba(0,0,0,.25)" }} />}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <div className="pr-same-number">
                <Checkbox checked={sameAsMobile} onChange={handleSameAsMobileChange}>
                  WhatsApp number is the same as mobile number
                </Checkbox>
              </div>
            </div>

            <div className="pr-block">
              <SectionTitle title="Location" />
              <Row gutter={20} type="flex">
                <Col xs={24} data-pr-field="address">
                  <Form.Item
                    {...formItemLayout}
                    {...fieldItemProps("address")}
                    label="Full address"
                    required
                  >
                    <Input
                      placeholder="e.g. Building name, street, landmark, area"
                      size="large"
                      value={fullAddress}
                      onChange={handleFullAddressChange}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={20} type="flex" className="pr-three-col-row">
                <Col xs={24} md={8} data-pr-field="pincode">
                  <Form.Item
                    {...formItemLayout}
                    label="Pincode"
                    required
                    validateStatus={
                      fieldErrors.pincode ? "error" : pinError ? "error" : pinLookupReady ? "success" : ""
                    }
                    help={
                      fieldErrors.pincode ||
                      (pinError
                        ? "Invalid pincode"
                        : pinLoading
                          ? "Looking up pincode…"
                          : undefined)
                    }
                  >
                    <Spin spinning={pinLoading}>
                      <Input
                        placeholder="e.g. 673121"
                        size="large"
                        maxLength={6}
                        value={pincode}
                        onChange={handlePincodeChange}
                      />
                    </Spin>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} data-pr-field="city">
                  <Form.Item
                    {...formItemLayout}
                    {...fieldItemProps("city")}
                    label="City"
                    required
                  >
                    <Input
                      placeholder="e.g. Kalpetta"
                      size="large"
                      value={city}
                      onChange={handleCityChange}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item {...formItemLayout} label="District">
                    <Input
                      placeholder="Auto-filled from pincode"
                      size="large"
                      value={district}
                      readOnly
                      disabled={!pinLookupReady}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={20} type="flex" className="pr-three-col-row">
                <Col xs={24} md={8}>
                  <Form.Item {...formItemLayout} label="State">
                    <Input
                      placeholder="Auto-filled from pincode"
                      size="large"
                      value={state}
                      readOnly
                      disabled={!pinLookupReady}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item {...formItemLayout} label="Country">
                    <Input
                      placeholder="Auto-filled from pincode"
                      size="large"
                      value={country}
                      readOnly
                      disabled={!pinLookupReady}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div className="pr-block">
              <SectionTitle title="What services do you provide?" />
              <Row gutter={20} type="flex" className="pr-two-col-row">
                <Col xs={24} md={12} data-pr-field="serviceCategories">
                  <Form.Item
                    {...formItemLayout}
                    {...fieldItemProps("serviceCategories")}
                    label="Service categories"
                    required
                  >
                    <Spin spinning={serviceCategoriesLoading}>
                      <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        placeholder={
                          serviceCategoriesLoading
                            ? "Loading categories…"
                            : "Select service categories"
                        }
                        optionFilterProp="children"
                        size="large"
                        className="pr-service-categories"
                        style={{ width: "100%" }}
                        value={serviceCategories}
                        onChange={(value) => {
                          setServiceCategories(value);
                          clearFieldError("serviceCategories");
                        }}
                        notFoundContent={
                          serviceCategoriesLoading ? (
                            <Spin size="small" />
                          ) : (
                            "No categories found"
                          )
                        }
                      >
                        {serviceCategoryOptions.map((item) => (
                          <Option key={item} value={item}>
                            {item}
                          </Option>
                        ))}
                      </Select>
                    </Spin>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} data-pr-field="skillTags">
                  <Form.Item
                    {...formItemLayout}
                    {...fieldItemProps("skillTags")}
                    label="Partner Skill Tags"
                    required
                  >
                    <Spin spinning={skillOptionsLoading}>
                      <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        placeholder={
                          skillOptionsLoading ? "Loading skills…" : "Choose skills…"
                        }
                        optionFilterProp="children"
                        size="large"
                        className="pr-skill-select"
                        style={{ width: "100%" }}
                        value={skillTags}
                        onChange={(value) => {
                          setSkillTags(value);
                          clearFieldError("skillTags");
                        }}
                        notFoundContent={
                          skillOptionsLoading ? <Spin size="small" /> : "No skills found"
                        }
                      >
                        {skillOptions.map((item) => (
                          <Option key={item} value={item}>
                            {item}
                          </Option>
                        ))}
                      </Select>
                    </Spin>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={20} type="flex" className="pr-two-col-row">
                <Col xs={24} md={12} data-pr-field="serviceAreaCoverage">
                  <Form.Item
                    {...formItemLayout}
                    {...fieldItemProps("serviceAreaCoverage")}
                    label="Service area pincodes"
                    required
                  >
                    <Select
                      mode="tags"
                      size="large"
                      className="pr-coverage-pins"
                      style={{ width: "100%" }}
                      placeholder="Enter 6-digit pincodes and press Enter"
                      tokenSeparators={[",", " "]}
                      value={serviceAreaCoverage}
                      onChange={handleCoveragePincodesChange}
                      notFoundContent={null}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} data-pr-field="engineerCount">
                  <Form.Item
                    {...formItemLayout}
                    {...fieldItemProps("engineerCount")}
                    label="No. of engineers"
                    required
                  >
                    <InputNumber
                      min={1}
                      max={999}
                      precision={0}
                      placeholder="e.g. 5"
                      size="large"
                      style={{ width: "100%" }}
                      value={engineerCount}
                      onChange={(value) => {
                        setEngineerCount(value);
                        clearFieldError("engineerCount");
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div className="pr-block">
              <SectionTitle title="Store photos" optional />
              <div className="pr-upload-panel">
                <Upload.Dragger
                  multiple
                  accept="image/*"
                  fileList={storePhotos}
                  beforeUpload={handleStorePhotoUpload}
                  onRemove={handleStorePhotoRemove}
                  className="pr-dragger"
                >
                  <p className="ant-upload-drag-icon">
                    <Icon type="picture" style={{ color: "#7c3aed", fontSize: 48 }} />
                  </p>
                  <p className="ant-upload-text">Click or drag store photos here</p>
                  <p className="ant-upload-hint">JPG, PNG — multiple files allowed</p>
                </Upload.Dragger>
              </div>
            </div>

            <div className="pr-footer pr-footer--actions">
              {leadMeta.totalPending > 0 && leadMeta.currentRecord != null ? (
                <span className="pr-lead-counter">
                  {leadMeta.currentRecord + 1} of {leadMeta.totalPending} pending
                </span>
              ) : (
                <span className="pr-lead-counter" />
              )}
              <div className="pr-actions">
                <Button
                  size="large"
                  danger
                  loading={
                    actionLoading && loadingAction === "reject" && !rejectModalOpen
                  }
                  disabled={actionLoading || leadLoading}
                  onClick={handleReject}
                >
                  Reject
                </Button>
                <Button
                  size="large"
                  loading={
                    actionLoading &&
                    loadingAction === "callLater" &&
                    !callLaterModalOpen
                  }
                  disabled={actionLoading || leadLoading}
                  onClick={handleCallMeLater}
                >
                  Call me later
                </Button>
                <Button
                  type="primary"
                  size="large"
                  icon="check"
                  loading={actionLoading && loadingAction === "submit"}
                  disabled={actionLoading || leadLoading}
                  htmlType="submit"
                >
                  Submit
                </Button>
              </div>
            </div>
          </Form>
        </Card>
      </div>

      <Modal
        title="Reject lead"
        visible={rejectModalOpen}
        onOk={handleRejectConfirm}
        onCancel={handleRejectCancel}
        okText="Submit"
        confirmLoading={actionLoading && loadingAction === "reject"}
        cancelButtonProps={{ disabled: actionLoading && loadingAction === "reject" }}
        closable={!(actionLoading && loadingAction === "reject")}
        maskClosable={!(actionLoading && loadingAction === "reject")}
        destroyOnClose
      >
        <Form.Item label="Remarks" required {...formItemLayout} style={{ marginLeft: 0, marginRight: 0 }}>
          <Input.TextArea
            rows={3}
            value={rejectRemarks}
            onChange={(e) => setRejectRemarks(e.target.value)}
            placeholder="e.g. Customer not interested"
          />
        </Form.Item>
      </Modal>

      <Modal
        title="Call me later"
        visible={callLaterModalOpen}
        onOk={handleCallMeLaterConfirm}
        onCancel={handleCallMeLaterCancel}
        okText="Submit"
        confirmLoading={actionLoading && loadingAction === "callLater"}
        cancelButtonProps={{ disabled: actionLoading && loadingAction === "callLater" }}
        closable={!(actionLoading && loadingAction === "callLater")}
        maskClosable={!(actionLoading && loadingAction === "callLater")}
        destroyOnClose
      >
        <Form.Item
          label="Next contact date & time"
          required
          style={{ marginBottom: 16, marginLeft: 0, marginRight: 0 }}
          {...formItemLayout}
        >
          <DatePicker
            showTime={{ format: "HH:mm", defaultValue: moment("10:00", "HH:mm") }}
            style={{ width: "100%" }}
            format="DD-MM-YYYY hh:mm A"
            placeholder="Select date and time"
            value={nextContactDate}
            onChange={(value) => setNextContactDate(toISTMoment(value))}
            disabledDate={(current) => {
              if (!current) return false;
              return toISTMoment(current).isBefore(getNowIST().startOf("day"));
            }}
            disabledTime={(current) => {
              if (!current) return {};
              const selected = toISTMoment(current);
              const now = getNowIST();
              if (!selected || !selected.isSame(now, "day")) return {};

              const disabledHours = () =>
                Array.from({ length: 24 }, (_, hour) => hour).filter(
                  (hour) => hour < now.hour()
                );

              const disabledMinutes = (hour) => {
                if (hour > now.hour()) return [];
                return Array.from({ length: 60 }, (_, minute) => minute).filter(
                  (minute) => minute < now.minute()
                );
              };

              const disabledSeconds = (hour, minute) => {
                if (hour > now.hour() || minute > now.minute()) return [];
                return Array.from({ length: 60 }, (_, second) => second).filter(
                  (second) => second <= now.second()
                );
              };

              return { disabledHours, disabledMinutes, disabledSeconds };
            }}
          />
        </Form.Item>
        <Form.Item label="Remarks" required {...formItemLayout} style={{ marginLeft: 0, marginRight: 0 }}>
          <Input.TextArea
            rows={3}
            value={callLaterRemarks}
            onChange={(e) => setCallLaterRemarks(e.target.value)}
            placeholder="e.g. Customer asked to call after 3 days"
          />
        </Form.Item>
      </Modal>
    </div>
  );
};

export default PartnerRegistration;
